import ExcelJS from "exceljs";
import { lang } from "../../../utils/langs";

/**
 * ExcelBuilder
 * Utility to abstract the creation of Excel reports using ExcelJS.
 */
export class ExcelBuilder {
    constructor(metadata) {
        this.metadata = metadata;
        this.wb = new ExcelJS.Workbook();
        this.ws = this.wb.addWorksheet("Reporte");
    }

    /**
     * Adds the standardized header for accounting reports.
     */
    addMainHeader() {
        this.ws.mergeCells("A1:D1");
        const title = this.ws.getCell("A1");
        title.value = lang('es', this.metadata.real_type || this.metadata.report_type);
        title.font = { size: 16, bold: true, color: { argb: "FFFFFFFF" } };
        title.alignment = { vertical: "middle", horizontal: "center" };
        title.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E78" } };

        this.ws.addRow([]);
        this.ws.addRow([`Empresa: ${this.metadata.company_name || ""}`]);
        this.ws.addRow([`Periodo: ${this.metadata.period_from} → ${this.metadata.period_to}`]);
        this.ws.addRow([`Generado en: ${this.metadata.generated_at}`]);
        this.ws.addRow([]);
    }

    /**
     * Adds a section title with appropriate level styling.
     */
    addSectionTitle(title, level = 0) {
        const titleRow = this.ws.addRow([lang('es', title.replace(/_/g, " "))]);
        titleRow.font = { bold: true, size: 12 + (Math.max(0, 2 - level)) };
        if (level === 0) {
            titleRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } };
        }
    }

    /**
     * Adds a simple row.
     */
    addRow(data) {
        return this.ws.addRow(data);
    }

    /**
     * Adds a totals row with specific styling.
     */
    addTotalsRow(values, currencyIndices = []) {
        const row = this.ws.addRow(values);
        row.font = { bold: true };
        row.eachCell((cell, colNumber) => {
            if (currencyIndices.includes(colNumber - 1) && typeof cell.value === 'number') {
                cell.numFmt = '"$"#,##0;[Red]"-"$#,##0';
                cell.alignment = { horizontal: "right" };
            }
        });
        return row;
    }

    /**
     * Adds a table with headers and data.
     * @param {string[]} headers - Array of header strings.
     * @param {any[][]} rows - Matrix of row data.
     * @param {Object} options - Formatting options.
     */
    addTable(headers, rows, options = {}) {
        const headerRow = this.ws.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
            cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4472C4" } };
            cell.alignment = { horizontal: "center" };
        });

        rows.forEach((rowValues) => {
            const row = this.ws.addRow(rowValues);
            if (options.onRowAdded) {
                options.onRowAdded(row, rowValues);
            }
        });
    }

    /**
     * Finalizes the report, adjusts column widths and triggers download.
     */
    async download() {
        // Auto-adjust column widths
        this.ws.columns.forEach((col) => {
            let maxLength = 0;
            col.eachCell({ includeEmpty: true }, (cell) => {
                const length = cell.value ? cell.value.toString().length : 10;
                if (length > maxLength) maxLength = length;
            });
            col.width = maxLength + 2;
        });

        const buffer = await this.wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${this.metadata.report_type}_${this.metadata.company_name}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
    }
}
